import React, { Suspense, useState, useEffect } from "react";
import { Route, BrowserRouter as Router, Switch } from "react-router-dom";
import { CssBaseline, ThemeProvider } from "@material-ui/core";

import {
  MobileAuthenticatedRoute,
  MobileRoute,
  MobileMultifactorRoute,
  DesktopAuthenticatedRoute,
  DesktopAdminRoute,
  DesktopRoleRoute,
} from "./customRoutes";

import MobileLoading from "./components/MobileLoading";

import AppHome from "./pages/mobile/starting/Home.component";
import Introduction from "./pages/mobile/starting/Introduction.component";
import Welcome from "./pages/mobile/starting/Welcome";
import HavePlanQuestion from "./pages/mobile/starting/HavePlanQuestion.component";
import FPQQuestionary from "./pages/mobile/fpq/Questionary";
import MyQuotient from "./pages/mobile/fpq/MyQuotient";
import SignIn from "./pages/mobile/signin/SignIn.component";
import SignUp from "./pages/mobile/signup/SignUp.component";
import SignUpSuccess from "./pages/mobile/signupsuccess/SignUpSuccess.component";
import EmailConfirmation from "./pages/mobile/emailconfirmation/EmailConfirmation.component";
import Dashboard from "./pages/mobile/dashboard/Dashboard.component";
import PasswordRecovery from "./pages/mobile/passwordrecovery/PasswordRecovery.component";
import TokenResend from "./pages/mobile/tokenresend/TokenResend.component";
import RepLIQuestionary from "./pages/mobile/repli/Questionary.component";
import SaveResult from "./pages/mobile/repli/SaveResult.component";
import PasswordReset from "./pages/mobile/passwordreset/PasswordReset.component";
import MyAccountApp from "./pages/mobile/myaccount/MyAccount.component";
import Downloader from "./pages/mobile/repli/Downloader.component";
import MyMessageHome from "./pages/mobile/mymessages/Home.component";
import Profiler from "./pages/mobile/profiler/Profiler.component";
import Terms from "./pages/mobile/terms/TermsUsePrivacy.component";
import MyGoal from "./pages/mobile/mygoal/MyGoal.component";
import MyRepLI from "./pages/mobile/myrepli/MyRepLI.component";
import FeedBack from "./pages/mobile/feedback/feedback.component";
import FeedBackSubmitted from "./pages/mobile/feedback/feedbacksubmitted.component";
import Notification from "./pages/mobile/notification/Notification.component";
import MultiFactorAuthentication from "./pages/mobile/multifactorauthentication/multifactorauthentication.component";
import OTPAuthentication from "./pages/mobile/multifactorauthentication/otpauthentication.component";

import Login from "./pages/desktop/login/Login.component";
import ProviderPasswordRecovery from "./pages/desktop/passwordrecovery/PasswordRecovery.component";
import ProviderPasswordReset from "./pages/desktop/passwordreset/PasswordReset.component";
import ProviderTokenResend from "./pages/desktop/tokenresend/TokenResend.component";
import ProviderEmailConfirmation from "./pages/desktop/emailconfirmation/EmailConfirmation.component";
import RepLI from "./pages/desktop/repli/RepLI.component";
import Home from "./pages/desktop/home/Home.component";
import ProviderDashboard from "./pages/desktop/dashboard/ProviderDashboard.component";
import MyAccount from "./pages/desktop/myaccount/MyAccount.component";
import ReadUser from "./pages/desktop/user/Read.component";
import CreateUser from "./pages/desktop/user/Create.component";
import UpdateUser from "./pages/desktop/user/Update.component";
import ReadProvider from "./pages/desktop/provider/Read.component";
import PatientAssociation from "./pages/desktop/provider/PatientAssociation.component";
import MyPatients from "./pages/desktop/mypatients/MyPatients.component";
import CreatePatient from "./pages/desktop/patient/Create.component";
import UpdatePatient from "./pages/desktop/patient/Update.component";
import ReadMessage from "./pages/desktop/messaging/Read.component";
import CreateMessage from "./pages/desktop/messaging/Create.component";
import UpdateMessage from "./pages/desktop/messaging/Update.component";
import PatientProfile from "./pages/desktop/patientProfile/PatientProfile.component";

import mainTheme from "./themes/mainTheme";
import "./App.css";
import Messageing from "./pages/desktop/messaging/Messageing.component";
import Cookies from "universal-cookie";
import SaveFamilyHistory from "./pages/mobile/familyhistory/SaveFamilyHistory.component";
import Organizations from "./pages/desktop/organizations/Organizations";
import CreateOrganization from "./pages/desktop/organizations/CreateOrganization";
import UpdateOrganization from "./pages/desktop/organizations/UpdateOrganization";
import Bariatrics from "./pages/mobile/bariatrics/Bariatrics.component";

const cookies = new Cookies();

function App() {
  const [idleTime, setIdleTime] = useState(1);

  useEffect(() => {
    const idleInterval = setInterval(() => {
      setIdleTime((idleTime) => {
        if (idleTime < 30) {
          return idleTime + 1;
        } else {
          if (window.location.href.indexOf("provider") > -1) {
            cookies.remove("token", { path: "/" });
            window.location.replace("/provider/login");
          } else {
            localStorage.clear();
            cookies.remove("token", { path: "/" });
            window.location.replace("/app/signin");
          }
          return idleTime;
        }
      });
    }, 60000);

    return () => clearInterval(idleInterval);
  }, []);

  useEffect(() => {
    const refreshToken = setInterval(async () => {
      if (cookies.get("token") != null) {
        try {
          let response = await fetch(
            `${process.env.REACT_APP_API_URL}/${process.env.REACT_APP_API_REFRESH_TOKEN}`,
            {
              headers: {
                "X-ACCESS-TOKEN": cookies.get("token"),
              },
            }
          );
          let json = await response.json();
          if (!response.ok) {
            return;
          }
          cookies.set("token", json.token, {
            path: "/",
            expires: new Date(Date.now() + 7200000),
          });
        } catch (error) {
          console.error("Error refreshing token:", error);

          if (window.location.href.indexOf("provider") > -1) {
            cookies.remove("token", { path: "/" });
            window.location.replace("/provider/login");
          } else {
            localStorage.clear();
            cookies.remove("token", { path: "/" });
            window.location.replace("/app/signin");
          }
        }
      }
    }, 5400000);

    return () => clearInterval(refreshToken);
  }, []);

  window.addEventListener("keypress", (event) => {
    // handle keypress
    setIdleTime(0);
  });

  window.addEventListener("mousemove", (event) => {
    // handle keypress
    setIdleTime(0);
  });

  // logout from all the windows
  window.addEventListener("storage", function (event) {
    if (event.key === "logout-event") {
      localStorage.clear();
      cookies.remove("token", { path: "/" });
      window.location.replace("/app");
    } else if (event.key === "provider-logout-event") {
      localStorage.clear();
      cookies.remove("token", { path: "/" });
      window.location.replace("/provider/login");
    }
  });

  return (
    <Router>
      <ThemeProvider theme={mainTheme}>
        <CssBaseline />
        <div className="App">
          <Suspense fallback={<MobileLoading />}>
            <Switch>
              {/* Mobile app module routes */}
              <MobileRoute exact path="/" component={AppHome} />
              <MobileRoute exact path="/app" component={AppHome} />
              <MobileRoute exact path="/app/welcome" component={Welcome} />
              <MobileRoute exact path="/app/intro" component={Introduction} />
              <MobileRoute path="/app/terms" component={Terms} />
              <MobileRoute
                exact
                path="/app/haveplan"
                component={HavePlanQuestion}
              />
              <MobileRoute
                path="/app/fpqquestionary"
                component={FPQQuestionary}
              />
              <MobileRoute
                exact
                path="/app/fpqquotient/"
                component={MyQuotient}
              />
              <MobileRoute
                path="/app/fpqquotient/:curChildren/:desChildren/:bioChildren"
                component={MyQuotient}
              />
              <MobileRoute exact path="/app/signin" component={SignIn} />
              <MobileRoute
                path="/app/signin/pwdrecovery"
                component={PasswordRecovery}
              />
              <MobileRoute
                path="/app/signin/pwdreset/:token"
                component={PasswordReset}
              />
              <MobileRoute exact path="/app/signup" component={SignUp} />
              <MobileRoute
                path="/app/signup/success"
                component={SignUpSuccess}
              />
              <MobileRoute
                path="/app/signup/confirmation/:token"
                component={EmailConfirmation}
              />
              <MobileRoute path="/app/tokenresend" component={TokenResend} />

              <MobileMultifactorRoute
                exact
                path="/app/authentication"
                component={MultiFactorAuthentication}
              />
              <MobileMultifactorRoute
                exact
                path="/app/otpverification"
                component={OTPAuthentication}
              />

              <MobileAuthenticatedRoute
                path="/app/dashboard"
                component={Dashboard}
              />
              <MobileAuthenticatedRoute
                path="/app/repliquestionary"
                component={RepLIQuestionary}
              />
              <MobileAuthenticatedRoute
                path="/app/profiler"
                component={Profiler}
              />
              <MobileAuthenticatedRoute
                path="/app/replisave"
                component={SaveResult}
              />
              <MobileAuthenticatedRoute
                path="/app/myaccount"
                component={MyAccountApp}
              />
              <MobileAuthenticatedRoute
                path="/app/repli/send"
                component={Downloader}
              />
              <MobileAuthenticatedRoute
                exact
                path="/app/mymessage"
                component={MyMessageHome}
              />
              <MobileAuthenticatedRoute
                exact
                path="/app/mygoal"
                component={MyGoal}
              />
              <MobileAuthenticatedRoute
                exact
                path="/app/myrepli"
                component={MyRepLI}
              />
              <MobileAuthenticatedRoute
                exact
                path="/app/feedback"
                component={FeedBack}
              />
              <MobileAuthenticatedRoute
                exact
                path="/app/feedbacksubmitted"
                component={FeedBackSubmitted}
              />
              <MobileAuthenticatedRoute
                exact
                path="/app/notification"
                component={Notification}
              />
              <MobileAuthenticatedRoute
                exact
                path="/app/notification"
                component={Notification}
              />
              <MobileAuthenticatedRoute
                exact
                path="/app/familyhistory"
                component={SaveFamilyHistory}
              />
              <MobileAuthenticatedRoute
                exact
                path="/app/bariatrics"
                component={Bariatrics}
              />

              {/* Provider module routes */}
              <Route exact path="/provider/login" component={Login} />
              <Route
                path="/provider/login/pwdrecovery"
                component={ProviderPasswordRecovery}
              />
              <Route
                path="/provider/login/pwdreset/:token"
                component={ProviderPasswordReset}
              />
              <Route
                path="/provider/login/tokenresend"
                component={ProviderTokenResend}
              />
              <Route
                path="/provider/signup/confirmation/:token"
                component={ProviderEmailConfirmation}
              />
              {/* <DesktopAuthenticatedRoute exact path="/provider" component={Home} /> */}
              <DesktopAuthenticatedRoute
                exact
                path="/provider"
                component={ProviderDashboard}
              />
              <DesktopRoleRoute
                role="provider"
                path="/provider/repli/:patientId"
                component={RepLI}
              />
              <DesktopAuthenticatedRoute
                path="/provider/myaccount"
                component={MyAccount}
              />
              <DesktopAdminRoute
                exact
                path="/provider/user/"
                component={ReadUser}
              />
              <DesktopAdminRoute
                path="/provider/user/add"
                component={CreateUser}
              />
              <DesktopRoleRoute
                role="administrator"
                path="/provider/user/:userId"
                component={UpdateUser}
              />

              <DesktopAdminRoute
                exact
                path="/provider/organizations"
                component={Organizations}
              />
              <DesktopAdminRoute
                path="/provider/organizations/add"
                component={CreateOrganization}
              />
              <DesktopRoleRoute
                role="administrator"
                path="/provider/organizations/:orgId"
                component={UpdateOrganization}
              />

              <DesktopAdminRoute
                exact
                path="/provider/provider/"
                component={ReadProvider}
              />
              <DesktopAdminRoute
                path="/provider/provider/associate/:providerId"
                component={PatientAssociation}
              />
              <DesktopRoleRoute
                role="provider"
                path="/provider/provider/mypatients"
                component={MyPatients}
              />
              <DesktopRoleRoute
                role="provider"
                path="/provider/patient/profile/:patientId"
                component={PatientProfile}
              />
              <DesktopRoleRoute
                role="provider"
                path="/provider/patient/add"
                component={CreatePatient}
              />
              <DesktopRoleRoute
                role="provider"
                path="/provider/patient/:patientId"
                component={UpdatePatient}
              />
              <DesktopRoleRoute
                role="administrator"
                exact
                path="/provider/message"
                component={ReadMessage}
              />
              <DesktopRoleRoute
                role="administrator"
                path="/provider/message/add"
                component={CreateMessage}
              />
              <DesktopRoleRoute
                role="administrator"
                path="/provider/message/:messageId"
                component={UpdateMessage}
              />
              <DesktopRoleRoute
                role="provider"
                path="/provider/messaging"
                component={Messageing}
              />
            </Switch>
          </Suspense>
        </div>
      </ThemeProvider>
    </Router>
  );
}

export default App;
